import axios from "axios";

// 15s timeout: without this, axios's default (0 = never times out) means any
// backend/DB stall leaves the UI spinning forever with no error and no
// feedback. Bounding it turns a silent infinite hang into a real, catchable
// error the UI can show the user (see ErrorState usage in EmployeeDashboard).
const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
  timeout: 15000,
});

// Endpoints that must never trigger a refresh-and-retry: /auth/refresh is the
// refresh itself (retrying it would loop), and a 401 from /auth/login or
// /auth/logout means bad credentials or no session — not an expired one.
const NO_REFRESH = ["/auth/login", "/auth/refresh", "/auth/logout"];

// Fired once the refresh attempt has also failed, i.e. the session cannot be
// recovered. AuthContext listens for it.
export const SESSION_EXPIRED = "auth:session-expired";

// Shared across every request that 401s at the same moment, so a screen firing
// several calls in parallel (the dashboard fires four) sends one refresh rather
// than a stampede of four.
let refreshPromise = null;

// Every failed call gets one console line before anything else handles it.
// Most screens turn an error into a toast carrying only `detail`, which loses
// the status, the endpoint and the server's request id — so a screenshot of a
// failure was never enough to find the matching line in the server log. The id
// comes from the X-Request-ID header the backend sets on every response
// (logging_config.RequestLoggingMiddleware) and is the join key between the
// two sides.
function logApiFailure(error) {
  const { config, response, message, code } = error;
  const where = `${(config?.method || "?").toUpperCase()} ${config?.url || "?"}`;
  if (!response) {
    // Timeout, DNS failure, offline, or a CORS rejection — no status exists.
    console.error(`[api] ${where} failed with no response: ${code || ""} ${message || ""}`.trim());
    return;
  }
  const requestId = response.headers?.["x-request-id"] || "n/a";
  const detail = response.data?.detail;
  // A 401 is usually just the hourly access-cookie expiry, which the
  // interceptor below recovers from silently — noting it without crying wolf.
  const write = response.status === 401 ? console.info : console.error;
  write(`[api] ${where} -> ${response.status} (request id ${requestId})`,
    typeof detail === "string" ? detail : detail ?? "");
}

// The access cookie expires after 60 minutes by design; the refresh cookie is
// good for 7 days. Without this interceptor nothing ever traded one for the
// other, so a working session started returning 401 on the hour and dumped the
// user back at the login screen mid-task. Now that swap happens silently.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    logApiFailure(error);

    // No response at all = network error or timeout, not an auth problem.
    if (!response || response.status !== 401 || !config) return Promise.reject(error);
    if (config._retried) return Promise.reject(error);
    if (NO_REFRESH.some((path) => (config.url || "").startsWith(path))) {
      return Promise.reject(error);
    }

    config._retried = true;
    try {
      if (!refreshPromise) {
        refreshPromise = api.post("/auth/refresh").finally(() => { refreshPromise = null; });
      }
      await refreshPromise;
    } catch {
      // Refresh failed — the session really is over. Announce it so AuthContext
      // shows the login screen: without this the app keeps rendering pages whose
      // queries all 401, and screens with no error state (most list pages) look
      // like the data has been deleted rather than like a dead session.
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED));
      return Promise.reject(error);
    }
    return api(config);
  }
);

export function apiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
